/**
 * =============================================================================
 * LGBT RESOURCES WIDGET COMPONENT
 * =============================================================================
 * 
 * Floating widget component for searching and displaying LGBT+ resources 
 * from the LGBT National Help Center's database (glbtnearme.org).
 * 
 * Features:
 * - Floating button that expands to full search panel
 * - Search by ZIP code with adjustable radius
 * - Category filtering (16 categories)
 * - Youth Safe Mode (filters adult venues like bars/clubs)
 * - Multiple color themes
 * - LIST/MAP VIEW TOGGLE - Switch between list and map views
 * - "SEARCH THIS AREA" - Pan the map and search new areas dynamically
 * - Expandable category badges
 * - Responsive design with compact mode
 * - Easy website integration via embed code
 * 
 * =============================================================================
 */

import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './LGBTResourcesWidget.css';

// =============================================================================
// CONFIGURATION
// =============================================================================

/** API endpoint - uses environment variable or falls back to production URL */
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://lgbt-widget-backend.onrender.com';

// =============================================================================
// THEME DEFINITIONS
// =============================================================================

const THEME_COLORS = {
  purple: {
    primary: '#491994',
    primaryRgb: '73, 25, 148',
    secondary: '#5CB85C',
    secondaryRgb: '92, 184, 92',
    accent: '#af8bef',
    accentRgb: '175, 139, 239',
    light: '#7DCD7D',
    gradient: 'linear-gradient(135deg, #5CB85C 0%, #491994 100%)',
    gradientReverse: 'linear-gradient(135deg, #491994 0%, #5CB85C 100%)'
  },
  inverted: {
    primary: '#7DCD7D',
    primaryRgb: '125, 205, 125',
    secondary: '#af8bef',
    secondaryRgb: '175, 139, 239',
    accent: '#491994',
    accentRgb: '73, 25, 148',
    light: '#c9b3f7',
    gradient: 'linear-gradient(135deg, #af8bef 0%, #7DCD7D 100%)',
    gradientReverse: 'linear-gradient(135deg, #7DCD7D 0%, #af8bef 100%)'
  },
  monochrome: {
    primary: '#333333',
    primaryRgb: '51, 51, 51',
    secondary: '#666666',
    secondaryRgb: '102, 102, 102',
    accent: '#999999',
    accentRgb: '153, 153, 153',
    light: '#cccccc',
    gradient: 'linear-gradient(135deg, #666666 0%, #333333 100%)',
    gradientReverse: 'linear-gradient(135deg, #333333 0%, #666666 100%)'
  },
  ocean: {
    primary: '#1e3c72',
    primaryRgb: '30, 60, 114',
    secondary: '#2a5298',
    secondaryRgb: '42, 82, 152',
    accent: '#5b8cdf',
    accentRgb: '91, 140, 223',
    light: '#7ba3e8',
    gradient: 'linear-gradient(135deg, #2a5298 0%, #1e3c72 100%)',
    gradientReverse: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)'
  }
};

// =============================================================================
// CATEGORY COLORS FOR MAP MARKERS
// =============================================================================

const CATEGORY_COLORS = {
  'Crisis': '#FF3B30',
  'Crisis - Food Pantry': '#FF6B6B',
  'AIDS/HIV': '#E91E63',
  'Health': '#FF2D55',
  'Hotline': '#FF9500',
  'Bar/Club': '#E67E22',
  'Counseling': '#9C27B0',
  'Support': '#673AB7',
  'Youth': '#2196F3',
  'Student': '#03A9F4',
  'Senior': '#00BCD4',
  'Transgender': '#00CED1',
  'Community Center': '#009688',
  'Social': '#4CAF50',
  'Sports': '#8BC34A',
  'Cultural': '#CDDC39',
  'Religion': '#FFEB3B',
  'Political': '#FFC107',
  'Legal': '#795548',
  'Business': '#607D8B',
  'Fundraising': '#9E9E9E',
  'Media': '#3F51B5',
  'Bookstore': '#8D6E63',
  'Hotel': '#78909C',
  'Bisexual': '#D81B60',
  'Lesbian': '#E91E8C',
  'Restaurant': '#FF7043',
  'Recovery': '#6A1B9A',
  'default': '#491994'
};

// =============================================================================
// SOCIAL MEDIA PLATFORM DETECTION WITH BRAND ICONS
// =============================================================================

/**
 * Returns an SVG icon component for a given platform.
 * All icons are 16x16 and styled consistently.
 */
const PlatformIcon = ({ platform }) => {
  const iconStyle = {
    width: '16px',
    height: '16px',
    flexShrink: 0,
    display: 'inline-block',
    verticalAlign: 'middle'
  };

  switch (platform) {
    case 'instagram':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#E4405F">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      );
    case 'twitter':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#000000">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      );
    case 'facebook':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#1877F2">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      );
    case 'youtube':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#FF0000">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      );
    case 'tiktok':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#000000">
          <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
        </svg>
      );
    case 'linkedin':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#0A66C2">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      );
    case 'threads':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#000000">
          <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.59 12c.025 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.332-3.023.88-.73 2.088-1.146 3.396-1.17 1.078-.02 2.074.173 2.96.566l.025-.108c.072-.313.109-.682.109-1.097 0-1.408-.396-2.406-1.176-2.967-.652-.47-1.544-.669-2.726-.608-.895.046-1.63.097-2.252.672-.49.453-.81 1.14-.904 1.912l-2.006-.096c.073-1.347.57-2.471 1.439-3.249.966-.865 2.24-1.322 3.682-1.32 1.573-.006 2.824.403 3.72 1.216 1.01.915 1.523 2.29 1.523 4.086 0 .455-.044.88-.128 1.272l.022.01c1.222.596 2.282 1.474 2.897 2.888.773 1.781.744 4.548-1.496 6.74-1.924 1.883-4.292 2.704-7.453 2.736z"/>
        </svg>
      );
    case 'discord':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#5865F2">
          <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z"/>
        </svg>
      );
    case 'pinterest':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#BD081C">
          <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"/>
        </svg>
      );
    case 'snapchat':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#FFFC00">
          <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"/>
        </svg>
      );
    case 'tumblr':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#36465D">
          <path d="M14.563 24c-5.093 0-7.031-3.756-7.031-6.411V9.747H5.116V6.648c3.63-1.313 4.512-4.596 4.71-6.469C9.84.051 9.941 0 9.999 0h3.517v6.114h4.801v3.633h-4.82v7.47c.016 1.001.375 2.371 2.207 2.371h.09c.631-.02 1.486-.205 1.936-.419l1.156 3.425c-.436.636-2.4 1.374-4.156 1.404h-.178z"/>
        </svg>
      );
    case 'reddit':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#FF4500">
          <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
        </svg>
      );
    case 'meetup':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#ED1C40">
          <path d="M6.98 12.13c-.05.2-.1.39-.17.57-.26.64-.67 1.05-1.3 1.22-.61.17-1.18.04-1.66-.38-.18-.15-.33-.34-.5-.52.02-.05.03-.08.05-.1.49-.63.99-1.26 1.48-1.89.15-.18.29-.37.44-.55.32-.4.79-.53 1.27-.41.49.12.8.46.88.97.04.26.01.51-.07.76-.14.44-.28.88-.42 1.33zm2.33 1.02c.42.17.84.34 1.27.52-.01.1-.03.18-.05.27-.18.72-.38 1.44-.56 2.16-.11.43-.21.86-.34 1.28-.25.81-.93 1.32-1.76 1.33-.74.01-1.41-.45-1.68-1.16-.08-.2-.12-.43-.12-.65-.01-.79.03-1.59.05-2.38 0-.07.03-.15.06-.22.04-.1.1-.1.14.01.31.88.62 1.76.93 2.64.03.1.06.19.11.29.23.52.81.73 1.3.49.49-.25.73-.82.55-1.34-.18-.53-.36-1.05-.54-1.58-.08-.24-.15-.47-.23-.71-.02-.07-.04-.14-.06-.21-.04-.14.01-.2.14-.15.24.1.48.19.72.28.31.11.63.22.94.34.31.12.61.24.91.37.25.11.5.23.74.35-.04.1-.07.2-.11.3-.28.71-.56 1.43-.85 2.14-.2.5-.42.99-.61 1.49-.23.62-.73 1.02-1.39 1.11-.73.1-1.33-.18-1.74-.79-.22-.33-.33-.71-.32-1.1.02-.8.06-1.59.1-2.39.02-.42.05-.83.08-1.25.01-.1.04-.14.14-.1.57.23 1.15.47 1.72.7zm8.27-2.58c-.02.2-.04.4-.06.61-.12.92-.39 1.8-.79 2.64-.39.82-.91 1.55-1.57 2.17-.42.4-.88.73-1.42.96-.53.22-1.07.29-1.65.21-.51-.07-.94-.29-1.27-.67-.37-.42-.54-.91-.51-1.47.02-.4.12-.78.3-1.14.32-.64.77-1.19 1.28-1.68.78-.74 1.66-1.35 2.59-1.9.34-.2.68-.4 1.03-.58.05-.03.12-.04.18-.03.03 0 .07.06.08.1.06.26.08.52.08.78-.01.33-.02.67-.04 1zm2.77-.06c.13.19.11.41.06.63-.13.59-.33 1.16-.57 1.71-.38.86-.85 1.67-1.39 2.44-.36.52-.77 1-1.23 1.44-.52.5-1.11.89-1.8 1.13-.49.17-.99.25-1.51.17-.84-.14-1.4-.62-1.68-1.41-.08-.23-.12-.48-.13-.73-.02-.62.11-1.21.36-1.78.25-.58.58-1.11.99-1.58.55-.64 1.21-1.16 1.94-1.59.49-.29 1-.55 1.53-.75.65-.25 1.33-.4 2.03-.38.48.01.9.14 1.21.48.15.17.22.38.19.62-.04.4-.14.78-.31 1.14-.25.54-.59 1.03-1 1.46-.59.62-1.27 1.13-2.04 1.5-.04.02-.08.04-.13.05-.02.01-.05 0-.09-.01.02-.05.03-.09.06-.12.48-.55.87-1.14 1.16-1.8.17-.39.3-.8.39-1.22.03-.15.05-.31.06-.46.01-.11-.04-.17-.15-.18-.28-.01-.54.06-.8.15-.82.28-1.57.71-2.26 1.24-.6.46-1.11 1-1.52 1.63-.23.35-.41.73-.48 1.15-.03.16-.03.33-.02.5.02.28.18.45.46.49.24.03.47-.02.7-.1.58-.22 1.1-.55 1.57-.95.84-.73 1.52-1.58 2.06-2.55.26-.46.48-.94.65-1.44.12-.35.21-.71.26-1.08.02-.15 0-.3-.05-.44-.09-.26-.32-.34-.55-.21-.49.27-.95.58-1.37.93-.79.67-1.46 1.44-2.01 2.31-.08.12-.14.26-.21.39-.01.02-.02.05-.04.1-.04-.06-.08-.1-.1-.15-.22-.42-.25-.86-.11-1.31.16-.55.47-1.02.88-1.42.61-.59 1.32-1.04 2.1-1.39.56-.25 1.14-.44 1.74-.55.35-.07.7-.1 1.06-.06.26.03.49.12.67.33z"/>
        </svg>
      );
    case 'eventbrite':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#F05537">
          <path d="M16.296 12.763a5.47 5.47 0 0 1-.197 1.32H8.07c.276 1.376 1.392 2.367 2.77 2.367.904 0 1.727-.393 2.317-1.066l2.138 1.428c-1.05 1.393-2.666 2.188-4.455 2.188-3.122 0-5.51-2.544-5.51-5.735 0-3.19 2.388-5.734 5.51-5.734 2.994 0 5.456 2.544 5.456 5.232zM10.84 9.97c-1.26 0-2.308.874-2.663 2.112h5.162c-.236-1.238-1.282-2.112-2.5-2.112zM24 12c0 6.627-5.373 12-12 12S0 18.627 0 12 5.373 0 12 0s12 5.373 12 12z"/>
        </svg>
      );
    default:
      // Generic link icon
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
        </svg>
      );
  }
};

/**
 * Detects the social media platform from a URL and returns the platform key.
 * @param {string} url - The website URL to analyze
 * @returns {string} Platform key for icon lookup
 */
const getPlatformFromUrl = (url) => {
  if (!url) return 'default';
  
  const lowerUrl = url.toLowerCase();
  
  if (lowerUrl.includes('instagram.com') || lowerUrl.includes('instagr.am')) return 'instagram';
  if (lowerUrl.includes('twitter.com') || lowerUrl.includes('x.com')) return 'twitter';
  if (lowerUrl.includes('facebook.com') || lowerUrl.includes('fb.com') || lowerUrl.includes('fb.me')) return 'facebook';
  if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) return 'youtube';
  if (lowerUrl.includes('tiktok.com')) return 'tiktok';
  if (lowerUrl.includes('linkedin.com')) return 'linkedin';
  if (lowerUrl.includes('threads.net')) return 'threads';
  if (lowerUrl.includes('discord.gg') || lowerUrl.includes('discord.com')) return 'discord';
  if (lowerUrl.includes('pinterest.com') || lowerUrl.includes('pin.it')) return 'pinterest';
  if (lowerUrl.includes('snapchat.com')) return 'snapchat';
  if (lowerUrl.includes('tumblr.com')) return 'tumblr';
  if (lowerUrl.includes('reddit.com')) return 'reddit';
  if (lowerUrl.includes('meetup.com')) return 'meetup';
  if (lowerUrl.includes('eventbrite.com')) return 'eventbrite';
  
  return 'default';
};

/**
 * Gets the display label for a platform.
 * @param {string} platform - Platform key
 * @returns {string} Human-readable label
 */
const getPlatformLabel = (platform) => {
  const labels = {
    instagram: 'Instagram',
    twitter: 'Twitter',
    facebook: 'Facebook',
    youtube: 'YouTube',
    tiktok: 'TikTok',
    linkedin: 'LinkedIn',
    threads: 'Threads',
    discord: 'Discord',
    pinterest: 'Pinterest',
    snapchat: 'Snapchat',
    tumblr: 'Tumblr',
    reddit: 'Reddit',
    meetup: 'Meetup',
    eventbrite: 'Eventbrite',
    default: 'Website'
  };
  return labels[platform] || 'Website';
};

// =============================================================================
// ZOOM LEVEL <-> RADIUS MAPPING (matching Map component)
// =============================================================================

/**
 * Maps Leaflet zoom levels to search radius values.
 * Used for "Search This Area" feature to determine appropriate radius.
 */
const ZOOM_TO_RADIUS = {
  17: { level: 1, miles: 1 },   // Street level
  16: { level: 1, miles: 1 },
  15: { level: 2, miles: 5 },   // Neighborhood level
  14: { level: 2, miles: 5 },
  13: { level: 3, miles: 10 },  // City level
  12: { level: 3, miles: 10 },
  11: { level: 4, miles: 25 },  // Metro area level
  10: { level: 4, miles: 25 },
  9: { level: 5, miles: 50 },   // Regional level
  8: { level: 5, miles: 50 },
  7: { level: 6, miles: 100 },  // State level
  6: { level: 6, miles: 100 },
  5: { level: 6, miles: 100 },
};

/**
 * Gets the appropriate search radius info based on current zoom level.
 */
const getRadiusFromZoom = (zoom) => {
  const zoomLevels = Object.keys(ZOOM_TO_RADIUS).map(Number).sort((a, b) => b - a);
  const closestZoom = zoomLevels.find(z => zoom >= z) || zoomLevels[zoomLevels.length - 1];
  return ZOOM_TO_RADIUS[closestZoom];
};

/**
 * Calculates distance between two geographic points using the Haversine formula.
 */
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Reverse geocodes lat/lng coordinates to a ZIP code.
 */
const reverseGeocodeToZip = async (lat, lng) => {
  try {
    const response = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
    const data = await response.json();
    if (data && data.address && data.address.postcode) {
      const zip = data.address.postcode.split('-')[0];
      return zip;
    }
    return null;
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return null;
  }
};

// =============================================================================
// MAP HELPER COMPONENTS
// =============================================================================

/**
 * MapMovementTracker Component
 * 
 * Tracks map pan/zoom events and determines when to show "Search This Area" button.
 * Shows button when user has panned outside the original search radius.
 */
const MapMovementTracker = ({ 
  originalCenter, 
  originalRadius, 
  onMapMove, 
  onZoomChange,
  hasSearched 
}) => {
  const map = useMap();
  const moveTimeoutRef = useRef(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (moveTimeoutRef.current) {
        clearTimeout(moveTimeoutRef.current);
      }
    };
  }, []);

  // Subscribe to Leaflet map events
  useMapEvents({
    moveend: () => {
      if (!hasSearched || !originalCenter) return;
      
      if (moveTimeoutRef.current) {
        clearTimeout(moveTimeoutRef.current);
      }

      // Debounce: wait 500ms after movement stops
      moveTimeoutRef.current = setTimeout(() => {
        try {
          if (!map || !map.getCenter) return;
          
          const currentCenter = map.getCenter();
          const currentZoom = map.getZoom();
          
          if (!currentCenter) return;
          
          // Calculate how far user has moved from original search
          const distance = calculateDistance(
            originalCenter[0],
            originalCenter[1],
            currentCenter.lat,
            currentCenter.lng
          );

          const isOutsideRadius = distance > originalRadius;

          onMapMove({
            center: [currentCenter.lat, currentCenter.lng],
            zoom: currentZoom,
            isOutsideRadius,
            distance
          });
        } catch (e) {
          console.log('Map not ready or unmounted');
        }
      }, 500);
    },
    
    zoomend: () => {
      try {
        if (!map || !map.getZoom) return;
        const currentZoom = map.getZoom();
        onZoomChange(currentZoom);
      } catch (e) {
        // Map may have been unmounted
      }
    }
  });

  return null;
};

/**
 * Creates a custom Apple-style circular marker icon (matching Map component)
 */
const createColoredIcon = (color, isAdult = false, name = '', category = '') => {
  const size = 14;
  
  // Adult venue indicator - red ring around the marker
  const adultRing = isAdult ? `
    <div style="
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: ${size + 4}px;
      height: ${size + 4}px;
      border: 2px solid #FF3B30;
      border-radius: 50%;
      pointer-events: none;
    "></div>
  ` : '';
  
  return L.divIcon({
    html: `
      <div class="apple-marker-simple" style="position: relative; width: ${size + 10}px; height: ${size + 10}px;">
        ${adultRing}
        <div class="marker-circle" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: ${size}px; height: ${size}px; background: ${color}; border: 3px solid white; border-radius: 50%; box-shadow: 0 1px 3px rgba(0,0,0,0.4); cursor: pointer; transition: transform 0.2s;">
        </div>
        <div class="marker-tooltip">
          <div class="tooltip-name">${name}</div>
          ${category ? `<div class="tooltip-category">${category}</div>` : ''}
        </div>
      </div>
    `,
    className: 'custom-apple-marker',
    iconSize: [size + 10, size + 10],
    iconAnchor: [(size + 10)/2, (size + 10)/2],
    popupAnchor: [0, -(size + 10)/2]
  });
};

/**
 * Gets the color for a resource based on its category
 */
const getCategoryColor = (resource) => {
  const category = resource.category || (resource.categories && resource.categories[0]) || 'default';
  return CATEGORY_COLORS[category] || CATEGORY_COLORS.default;
};

/**
 * Component to control map view
 */
const MapController = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.setView(center, zoom, { animate: true });
    }
  }, [center, zoom, map]);
  
  return null;
};

// ============================================================================
// POSTAL CODE HELPERS
// ============================================================================
// Supports both US ZIP codes (12345 or 12345-6789) and Canadian postal codes
// (A1A 1A1 or A1A1A1). Added May 2026 in response to Aaron's report that
// Canadian visitors could not search.
// ============================================================================

const US_ZIP_REGEX = /^\d{5}(-\d{4})?$/;
const CA_POSTAL_REGEX = /^[A-Za-z]\d[A-Za-z]\s*\d[A-Za-z]\d$/;

/**
 * Returns true if the given string is a valid US ZIP or Canadian postal code.
 */
const isValidPostalCode = (code) => {
  if (!code) return false;
  const trimmed = code.trim();
  return US_ZIP_REGEX.test(trimmed) || CA_POSTAL_REGEX.test(trimmed);
};

/**
 * Returns 'US' or 'CA' depending on which format the code matches, or null.
 */
const getPostalCountry = (code) => {
  if (typeof code !== 'string') return null;
  const trimmed = code.trim();
  if (US_ZIP_REGEX.test(trimmed)) return 'US';
  if (CA_POSTAL_REGEX.test(trimmed)) return 'CA';
  return null;
};

/**
 * Normalizes a postal code for sending upstream.
 *
 * Canadian codes: STRIP any space and uppercase ("M5V 3A8" -> "M5V3A8").
 * Required because Tim's API (confirmed 22 May 2026) only matches
 * Canadian codes when sent as a 6-character no-space string.
 *
 * US ZIP+4: STRIP the +4 extension ("10011-1234" -> "10011"). Tim's API
 * only handles 5-digit US ZIPs, so we drop the extension and search the
 * 5-digit area. Better than returning 0 results to the user.
 *
 * US 5-digit ZIPs: returned trimmed and uppercased.
 */
const normalizePostalCode = (code) => {
  if (typeof code !== 'string') return '';
  const trimmed = code.trim().toUpperCase();
  if (/^[A-Z]\d[A-Z]\s*\d[A-Z]\d$/.test(trimmed)) {
    return trimmed.replace(/\s+/g, '');
  }
  if (/^\d{5}-\d{4}$/.test(trimmed)) {
    return trimmed.slice(0, 5);
  }
  return trimmed;
};

/**
 * Geocode a US ZIP or Canadian postal code to coordinates via Nominatim.
 */
const geocodeZipCode = async (zip) => {
  try {
    const country = getPostalCountry(zip) || 'US';
    const normalized = normalizePostalCode(zip);
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?postalcode=${encodeURIComponent(normalized)}&country=${country}&format=json&limit=1`
    );
    const data = await response.json();
    if (data && data.length > 0) {
      return {
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon)
      };
    }
    return null;
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
};

// =============================================================================
// SUBCATEGORY CONFIGURATION
// =============================================================================
// Curated category -> subcategory mapping from live analysis of Tim's API
// (find-all-subcategories-v2.js, July 2026). Junk single-letter values
// ("g", "n", "r", "t") and one-off values removed. Strings must EXACTLY
// match the API's subtype values, including existing typos in Tim's data
// (e.g. "Police Liason") -- do not correct spellings or filtering breaks.
// NOTE: this block is duplicated in LGBTResourcesWidget.js and
// LGBTResourcesMap.js -- keep both in sync when editing.
// =============================================================================

const ALL_SUBCATEGORIES_LABEL = 'All Subcategories';

// =============================================================================
// Sub-category SEARCH is disabled (Aug 2026): Tim wants sub-categories shown
// on the result cards themselves, not offered as a search filter.
// Flip this to true to bring the search dropdown back — everything underneath
// still works (backend filter support was left in place).
// =============================================================================
const SHOW_SUBCATEGORY_SEARCH = false;

/**
 * Build the combined Category-Subcategory badge labels for a resource,
 * per Brad's spec (Aug 2026): e.g. "Media-Magazine", "Social-Gay Men".
 *
 * REWRITTEN 5 Aug 2026: now built from the positional type1..8/subtype1..8
 * pairs (forwarded by the backend in `resource.types`), which are stable
 * across every query mode. The previous version read Tim's `subcategories`
 * array, but that field is QUERY-RELATIVE — it only contains the subtypes
 * belonging to the category you searched under — which caused the
 * inconsistent badges Brad reported on 4 Aug 2026 (23.5% of resources
 * showed different subcategory data depending on the search; see
 * scripts/diagnose-subcategory-inconsistency.js in the backend).
 *
 * Rules:
 *  - Each (type_i, subtype_i) pair becomes one "Type-Subtype" bubble.
 *  - Junk subtype codes (single chars like "t"/"n"/"g" in Tim's data) and
 *    subtypes identical to their type are treated as no subtype.
 *  - A type with no valid subtype gets a plain bubble, but only if that
 *    type has no combined bubble already (no bare "Social" beside
 *    "Social-Gay Men").
 *  - Pairs are deduped case-insensitively, original order preserved.
 *
 * NOTE: duplicated in LGBTResourcesWidget.js and LGBTResourcesMap.js —
 * keep both in sync. Unit tests with real API fixtures live in
 * lgbt-resources-widget-backend/scripts/test-badge-builder.js.
 *
 * @param {Object} resource - Formatted resource from the backend
 * @returns {string[]} Badge labels in display order
 */
const buildCategoryBadgeLabels = (resource) => {
  if (!resource) return [];

  // Backend forwards raw pairs nested under `types`; fall back to flat
  // fields in case a raw API-shaped object ever reaches this function.
  const pairSource = resource.types || resource;

  const combined = [];          // { label, typeKey }
  const bareTypes = [];         // { label, key }
  const seenPairKeys = new Set();
  const typesWithPairs = new Set();

  for (let i = 1; i <= 8; i++) {
    const rawType = pairSource[`type${i}`];
    const rawSub = pairSource[`subtype${i}`];

    const type = typeof rawType === 'string' ? rawType.trim() : '';
    if (!type) continue;

    const sub = typeof rawSub === 'string' ? rawSub.trim() : '';
    const typeKey = type.toLowerCase();

    // Junk filter: Tim's data contains stray single-char subtype codes
    // ("t", "n", "g") that his own cleaning also strips. Also skip
    // subtypes identical to their type ("Lesbian-Lesbian").
    const subIsValid = sub.length >= 2 && sub.toLowerCase() !== typeKey;

    if (subIsValid) {
      const pairKey = `${typeKey}|${sub.toLowerCase()}`;
      if (!seenPairKeys.has(pairKey)) {
        seenPairKeys.add(pairKey);
        combined.push({ label: `${type}-${sub}`, typeKey });
      }
      typesWithPairs.add(typeKey);
    } else if (!bareTypes.some((b) => b.key === typeKey)) {
      bareTypes.push({ label: type, key: typeKey });
    }
  }

  // Fallback for payloads with no type1..8 data at all: keep the old
  // behaviour of plain category bubbles.
  if (combined.length === 0 && bareTypes.length === 0) {
    return resource.categories || (resource.category ? [resource.category] : []);
  }

  const labels = combined.map((c) => c.label);
  bareTypes.forEach((bare) => {
    if (!typesWithPairs.has(bare.key)) labels.push(bare.label);
  });
  return labels;
};

/**
 * Sub-categories that are hidden from the dropdown in Youth Safe Mode.
 * The backend still enforces blocking via YouthBlock + blocked combinations;
 * this just avoids offering adult-oriented filters in the youth-safe UI.
 */
const ADULT_SUBCATEGORIES = [
  'Erotic',
  'Sex',
  'Fetish',
  'Levi/Leather',
  'Leather',
  'S/M',
  'Naturist',
  'Bears',
  'Cannabis',
  'Dating',
  'Motorcycle',
];

/**
 * Keyed by the widget's dropdown category names (not the API names —
 * e.g. key is 'AIDS/HIV' even though the API category is 'AIDS').
 * Ordered roughly by how common each subtype is in the live data.
 */
const SUBCATEGORIES = {
  'AIDS/HIV': [
    'Testing', 'Support', 'ASO', 'Clinic', 'Medical', 'Education',
    'Counseling', 'Treatment', 'Social', 'Food Pantry', 'Housing',
    'Helpline', 'Meals', 'Shelter',
  ],
  'Bar/Club': [
    'GLBT', 'Gay Men', 'Gay Straight', 'Dance', 'Neighborhood',
    'Cabaret/Piano', 'Leather', 'Lesbian', 'Sports', 'Video', 'Western',
    'Drag', 'Transgender',
  ],
  'Bisexual': [
    'Social', 'Support', 'Dating', 'Internet',
  ],
  'Bookstore': [
    'GLBT', 'Feminist', 'Mail Order', 'Cards/Gifts', 'Hobbies', 'Coffee Shop',
  ],
  'Business': [
    'Fashion', 'Mail Order', 'Cards/Gifts', 'Salons', 'Tattoo',
    'Employee Group', 'Coaching', 'Gyms', 'Travel', 'Marketing/PR',
    'Real Estate', 'Film', 'Internet', 'Jewelry', 'Wedding', 'Pets',
    'Chamber of Commerce', 'Financial', 'Erotic', 'Dating', 'Cannabis',
  ],
  'Community Center': [
    'Support', 'Food Pantry', 'Social', 'Pride', 'Medical', 'Counseling',
    'Testing', 'Education',
  ],
  'Counseling': [
    'Support', 'Bilingual', 'Alcohol', 'Narcotics', 'Coaching',
  ],
  'Crisis': [
    'Food Pantry', 'Support', 'Violence', 'Shelter', 'Counseling', 'Hotline',
    'Education', 'Runaway', 'Suicide', 'Youth Shelter', 'Meals', 'Testing',
  ],
  'Cultural': [
    'Film', 'Theater', 'Archive', 'Dance', 'Arts', 'Chorus', 'Video',
    'Band', 'Activist',
  ],
  'Fundraising': [
    'Support', 'Education', 'Activist', 'Social', 'Theater', 'Scholarships',
  ],
  'Health': [
    'Medical', 'Clinic', 'Transgender', 'Testing', 'Primary Care',
    'Chiropractor', 'Dentist', 'Support', 'Treatment', 'Internal Medicine',
    'Alternative', 'Surgery', 'Adolescent', 'Gynecologist', 'Veterinarian',
    'Counseling',
  ],
  'Hotel': [
    'GLBT', 'Gay Men', 'Campground',
  ],
  'Hotline': [
    'Crisis', 'Support', 'Violence', 'Suicide', 'Helpline', 'Youth', 'GLBT',
  ],
  'Legal': [
    'Family', 'Discrimination', 'Immigration', 'General', 'Will/Estates',
    'Real Estate', 'Police Liason', 'Name Change', 'Divorce', 'Business',
    'Criminal', 'Negligence', 'Referral',
  ],
  'Lesbian': [
    'Social', 'Internet', 'Support', 'Magazine', 'Activist',
  ],
  'Media': [
    'Internet', 'Magazine', 'Publisher', 'Film', 'Video', 'Press', 'Dating',
    'Directory', 'Archive', 'TV/Cable', 'Radio', 'Newsletter',
  ],
  'Political': [
    'Activist', 'Democrat', 'Lobbying',
  ],
  'Recovery': [
    'Alcohol', 'Narcotics', 'Sexual', 'Overeaters',
  ],
  'Religion': [
    'UCC', 'Episcopalian', 'Lutheran', 'Unitarian', 'Methodist',
    'Presbyterian', 'Christian', 'Jewish', 'Baptist', 'Food Pantry',
    'Disciples', 'Spiritual', 'Catholic', 'MCC', 'Anabaptist', 'Seminary',
    'Islam', 'Atheist/Humanist',
  ],
  'Restaurant': [
    'Coffee Shop', 'Gay Straight', 'Catering',
  ],
  'Senior': [
    'Support', 'Social', 'Meals', 'Disability', 'Housing', 'Legal', 'Medical',
  ],
  'Social': [
    'GLBT', 'Pride', 'Gay Men', 'Lesbian', 'Bi', 'Hobbies', 'POC', 'Dance',
    'Asian', 'Latino/a', 'Family', 'Theater', 'Outdoors', 'Dating',
    'Levi/Leather', 'S/M', 'Fetish', 'Motorcycle', 'Naturist', 'Bears',
    'Gaymer', 'Square Dance', 'Spiritual', 'Jewish', 'Bicycling',
  ],
  'Sports': [
    'Running', 'Outdoors', 'Bicycling', 'Bowling', 'Tennis', 'Football',
    'Softball', 'Volleyball', 'Yoga', 'Cheerleading', 'Basketball', 'Rugby',
    'Sailing', 'Swimming', 'Hockey', 'Soccer', 'Golf', 'Rodeo', 'Scuba',
  ],
  'Student': [
    'High School', 'Social', 'Support', 'Resource Center', 'Scholarships',
    'Alumni', 'Academic', 'Under 13', 'K to 8', 'Faculty', 'Fraternity',
  ],
  'Support': [
    'Family', 'GLBT', 'POC', 'Social', 'Bilingual', 'Lesbian', 'Gay Men',
    'Relocation', 'Disability', 'Seniors', 'Latino/a', 'Asian', 'Bi',
    'Asexual', 'Immigration', 'Intersex', 'Military', 'Marriage', 'Prison',
  ],
  'Transgender': [
    'Support', 'Medical', 'Clinic', 'Testing', 'Counseling', 'Social',
    'Youth', 'Family', 'Masculine', 'Feminine', 'Non binary', 'FTM',
    'Political', 'Legal', 'Binder/Breastform', 'Surgery', 'Name Change',
    'Relocation',
  ],
  'Youth': [
    'Support', 'Social', 'High School', 'Counseling', 'Education',
    'Under 13', 'Runaway', 'Food Pantry', 'Shelter', 'Family', 'Crisis',
    'Medical', 'K to 8', 'Camp', 'Youth Shelter', 'Pride',
  ],
};

/**
 * Returns the sub-category list for a category, filtered for Youth Safe Mode
 * if applicable. Returns null when the category has no sub-categories.
 *
 * @param {string} category - The selected dropdown category
 * @param {boolean} youthSafe - Whether Youth Safe Mode is active
 * @returns {string[]|null}
 */
const getSubcategoriesFor = (category, youthSafe = false) => {
  const subs = SUBCATEGORIES[category];
  if (!subs || subs.length === 0) return null;
  if (!youthSafe) return subs;
  const filtered = subs.filter((s) => !ADULT_SUBCATEGORIES.includes(s));
  return filtered.length > 0 ? filtered : null;
};


// =============================================================================
// MAIN COMPONENT
// =============================================================================

const LGBTResourcesWidget = ({ 
  apiKey = 'demo', 
  defaultOpen = false, 
  hideButton = false,
  youthSafe = false,
  theme = 'purple' 
}) => {
  
  // ===========================================================================
  // STATE MANAGEMENT
  // ===========================================================================
  
  // UI state
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isCompact, setIsCompact] = useState(false);
  const [isSearchCollapsed, setIsSearchCollapsed] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedDetails, setExpandedDetails] = useState({});
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  
  // Search state
  const [searchParams, setSearchParams] = useState({
    zip: '',
    category: 'All Categories',
    subcategory: ALL_SUBCATEGORIES_LABEL,
    radius: 5
  });
  const [includeAdult, setIncludeAdult] = useState(!youthSafe);
  const [searchCount, setSearchCount] = useState(0);
  
  // Results state
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Map state
  const [mapCenter, setMapCenter] = useState([39.8283, -98.5795]);
  const [mapZoom, setMapZoom] = useState(4);
  
  // "Search This Area" feature state
  const [originalSearchCenter, setOriginalSearchCenter] = useState(null);
  const [originalSearchRadius, setOriginalSearchRadius] = useState(null);
  const [showSearchHereButton, setShowSearchHereButton] = useState(false);
  const [currentViewportCenter, setCurrentViewportCenter] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(4);
  const [searchingNewArea, setSearchingNewArea] = useState(false);
  
  // DOM refs
  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  // ===========================================================================
  // EVENT HANDLERS
  // ===========================================================================

  const toggleExpandedCategories = (e, resourceId) => {
    e.stopPropagation();
    setExpandedCategories(prev => ({
      ...prev,
      [resourceId]: !prev[resourceId]
    }));
  };

  const toggleExpandedDetails = (e, resourceId) => {
    e.stopPropagation();
    setExpandedDetails(prev => ({
      ...prev,
      [resourceId]: !prev[resourceId]
    }));
  };

  // ===========================================================================
  // CATEGORY CONFIGURATION
  // ===========================================================================

  const allCategories = [
    'All Categories',
    'AIDS/HIV',
    'Bar/Club',
    'Bisexual',
    'Bookstore',
    'Business',
    'Community Center',
    'Counseling',
    'Crisis',
    'Crisis - Food Pantry',
    'Cultural',
    'Fundraising',
    'Health',
    'Hotel',
    'Hotline',
    'Legal',
    'Lesbian',
    'Media',
    'Political',
    'Recovery',
    'Religion',
    'Restaurant',
    'Senior',
    'Social',
    'Sports',
    'Student',
    'Support',
    'Transgender',
    'Youth'
  ];
  
  const categories = youthSafe 
    ? allCategories.filter(cat => cat !== 'Bar/Club')
    : allCategories;

  // ===========================================================================
  // THEME APPLICATION
  // ===========================================================================

  useEffect(() => {
    const themeColors = THEME_COLORS[theme] || THEME_COLORS.purple;
    
    if (panelRef.current) {
      const panel = panelRef.current;
      panel.style.setProperty('--theme-primary', themeColors.primary);
      panel.style.setProperty('--theme-primary-rgb', themeColors.primaryRgb);
      panel.style.setProperty('--theme-secondary', themeColors.secondary);
      panel.style.setProperty('--theme-secondary-rgb', themeColors.secondaryRgb);
      panel.style.setProperty('--theme-accent', themeColors.accent);
      panel.style.setProperty('--theme-accent-rgb', themeColors.accentRgb);
      panel.style.setProperty('--theme-light', themeColors.light);
      panel.style.setProperty('--theme-gradient', themeColors.gradient);
      panel.style.setProperty('--theme-gradient-reverse', themeColors.gradientReverse);
    }
    
    if (buttonRef.current) {
      const button = buttonRef.current;
      button.style.setProperty('--theme-primary', themeColors.primary);
      button.style.setProperty('--theme-primary-rgb', themeColors.primaryRgb);
      button.style.setProperty('--theme-secondary', themeColors.secondary);
      button.style.setProperty('--theme-secondary-rgb', themeColors.secondaryRgb);
      button.style.setProperty('--theme-accent', themeColors.accent);
      button.style.setProperty('--theme-accent-rgb', themeColors.accentRgb);
      button.style.setProperty('--theme-light', themeColors.light);
      button.style.setProperty('--theme-gradient', themeColors.gradient);
      button.style.setProperty('--theme-gradient-reverse', themeColors.gradientReverse);
    }
  }, [theme, isOpen]);

  useEffect(() => {
    if (defaultOpen) {
      setIsOpen(true);
    }
  }, [defaultOpen]);

  // ===========================================================================
  // FORM HANDLERS
  // ===========================================================================

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchParams({
      ...searchParams,
      [name]: name === 'radius' ? parseInt(value) : value,
      // Changing the parent category resets the subcategory — the old
      // subcategory may not exist under the newly selected category
      ...(name === 'category' ? { subcategory: ALL_SUBCATEGORIES_LABEL } : {})
    });
  };

  // ===========================================================================
  // SEARCH FUNCTIONALITY
  // ===========================================================================

  const handleSearch = async () => {
    if (!isValidPostalCode(searchParams.zip)) {
      setError('Please enter a valid ZIP code (12345) or Canadian postal code (A1A 1A1)');
      return;
    }

    const normalizedZip = normalizePostalCode(searchParams.zip);

    setLoading(true);
    setError(null);
    setResources([]);
    setExpandedCategories({});
    setIsCompact(true);
    setShowSearchHereButton(false); // Reset search here button on new search

    try {
      // Geocode ZIP for map
      const zipCenter = await geocodeZipCode(normalizedZip);
      if (zipCenter) {
        setMapCenter([zipCenter.lat, zipCenter.lng]);
        // Store original search center and radius for "Search This Area" feature
        setOriginalSearchCenter([zipCenter.lat, zipCenter.lng]);
        setOriginalSearchRadius(searchParams.radius);
        
        // Zoom levels matching the Map component
        let zoomLevel;
        if (searchParams.radius <= 1) zoomLevel = 16;       // Street level
        else if (searchParams.radius <= 5) zoomLevel = 14;  // Neighborhood level
        else if (searchParams.radius <= 10) zoomLevel = 13; // City level
        else if (searchParams.radius <= 25) zoomLevel = 11; // Metro area level
        else if (searchParams.radius <= 50) zoomLevel = 9;  // Regional level
        else zoomLevel = 8;                                  // State level
        setMapZoom(zoomLevel);
        setCurrentZoom(zoomLevel);
      }

      const response = await fetch(`${API_BASE_URL}/api/resources`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey
        },
        body: JSON.stringify({
          zip: normalizedZip,
          radius: searchParams.radius,
          category: searchParams.category,
          subcategory: searchParams.subcategory !== ALL_SUBCATEGORIES_LABEL ? searchParams.subcategory : null,
          includeAdult: includeAdult,
          apiKey: apiKey
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Server error (${response.status})`);
      }

      const data = await response.json();
      
      if (data.resources && data.resources.length > 0) {
        const processedResources = data.resources.map((resource, index) => ({
          ...resource,
          id: resource.id || resource.idnum || index,
          coordinates: resource.latitude && resource.longitude ? {
            lat: parseFloat(resource.latitude),
            lng: parseFloat(resource.longitude)
          } : null
        }));
        
        setResources(processedResources);
        setHasSearched(true);
        setIsSearchCollapsed(true);
        setSearchCount(prev => prev + 1);
      } else {
        setError(null);
        setResources([]);
        setHasSearched(true);
        setSearchCount(prev => prev + 1);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Unable to load resources. Please try again later.');
      setResources([]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      handleSearch();
    }
  };

  // ===========================================================================
  // "SEARCH THIS AREA" FEATURE
  // ===========================================================================

  /**
   * Handles the "Search This Area" button click.
   * Reverse geocodes the current map center and searches for resources there.
   * Merges new results with existing ones.
   */
  const handleSearchThisArea = async () => {
    if (!currentViewportCenter) return;

    setSearchingNewArea(true);
    setShowSearchHereButton(false);

    try {
      const zip = await reverseGeocodeToZip(currentViewportCenter[0], currentViewportCenter[1]);
      
      if (!zip) {
        setError('Unable to determine location. Please try again.');
        setSearchingNewArea(false);
        return;
      }

      const radiusInfo = getRadiusFromZoom(currentZoom);
      const searchRadius = radiusInfo.miles;

      // Update the original search center to the new location
      setOriginalSearchCenter(currentViewportCenter);
      setOriginalSearchRadius(searchRadius);

      const response = await fetch(`${API_BASE_URL}/api/resources`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey
        },
        body: JSON.stringify({
          zip: zip,
          radius: searchRadius,
          category: searchParams.category,
          subcategory: searchParams.subcategory !== ALL_SUBCATEGORIES_LABEL ? searchParams.subcategory : null,
          includeAdult: includeAdult,
          apiKey: apiKey
        })
      });

      if (!response.ok) throw new Error('Failed to search this area');

      const data = await response.json();

      if (data.resources && data.resources.length > 0) {
        const newResources = data.resources
          .filter(resource => resource.latitude && resource.longitude)
          .map((resource, index) => ({
            ...resource,
            id: resource.id || resource.idnum || `new-${index}-${Date.now()}`,
            coordinates: {
              lat: parseFloat(resource.latitude),
              lng: parseFloat(resource.longitude)
            }
          }));

        // Merge new resources with existing ones (don't replace)
        setResources(prevResources => {
          const existingIds = new Set(prevResources.map(r => r.id));
          const uniqueNewResources = newResources.filter(r => !existingIds.has(r.id));
          return [...prevResources, ...uniqueNewResources];
        });
      }
    } catch (err) {
      console.error('Search this area error:', err);
    } finally {
      setSearchingNewArea(false);
    }
  };

  /**
   * Handles map movement events from MapMovementTracker.
   */
  const handleMapMove = (moveData) => {
    const { center, isOutsideRadius } = moveData;
    setCurrentViewportCenter(center);
    setShowSearchHereButton(isOutsideRadius && !searchingNewArea);
  };

  /**
   * Handles zoom changes from MapMovementTracker.
   */
  const handleZoomChange = (zoom) => {
    setCurrentZoom(zoom);
  };

  const handleClose = () => {
    setIsOpen(false);
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'close-widget' }, '*');
    }
  };

  const themeColors = THEME_COLORS[theme] || THEME_COLORS.purple;
  
  const resourcesWithCoords = resources.filter(r => 
    r.coordinates && r.coordinates.lat && r.coordinates.lng
  );

  return (
    <>
      {/* Floating Button with Curved Text */}
      {!hideButton && (
        <div className={`widget-button-wrapper ${isOpen ? 'wrapper-hidden-text' : ''}`}>
          {/* Curved SVG text around the button */}
          <svg 
            className="widget-curved-text" 
            viewBox="0 0 120 120" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <path id="widget-top-arc" d="M 15,60 A 45,45 0 0,1 105,60" fill="none" />
              <path id="widget-bottom-arc" d="M 15,60 A 45,45 0 0,0 105,60" fill="none" />
            </defs>
            <text 
              fontWeight="800" 
              fontSize="14" 
              letterSpacing="1"
              paintOrder="stroke"
              stroke="#491994"
              strokeWidth="3"
              strokeLinejoin="round"
              fill="white"
            >
              <textPath href="#widget-top-arc" startOffset="50%" textAnchor="middle">find lgbtq+</textPath>
            </text>
            <text 
              fontWeight="800" 
              fontSize="14" 
              letterSpacing="1"
              paintOrder="stroke"
              stroke="#491994"
              strokeWidth="3"
              strokeLinejoin="round"
              fill="white"
            >
              <textPath href="#widget-bottom-arc" startOffset="50%" textAnchor="middle">resources here</textPath>
            </text>
          </svg>

          <button 
            ref={buttonRef}
            className={`widget-floating-button ${isOpen ? 'widget-button-active' : ''}`}
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Close LGBT Resources Widget" : "Open LGBT Resources Widget"}
            aria-expanded={isOpen}
            style={{
              '--theme-primary': themeColors.primary,
              '--theme-secondary': themeColors.secondary,
              '--theme-accent': themeColors.accent,
              '--theme-gradient': themeColors.gradient
            }}
          >
            <div className="widget-button-logo">
              <img src="/logo192.png" alt="LGBT Resources" />
            </div>
            {isOpen && <span className="widget-button-indicator">✕</span>}
          </button>
        </div>
      )}

      {/* Widget Panel */}
      {isOpen && (
        <div className={`widget-overlay ${defaultOpen ? 'widget-embedded' : ''}`}>
          <div 
            ref={panelRef}
            className={`widget-panel ${isCompact ? 'widget-compact' : ''}`}
            style={{
              '--theme-primary': themeColors.primary,
              '--theme-primary-rgb': themeColors.primaryRgb,
              '--theme-secondary': themeColors.secondary,
              '--theme-secondary-rgb': themeColors.secondaryRgb,
              '--theme-accent': themeColors.accent,
              '--theme-accent-rgb': themeColors.accentRgb,
              '--theme-light': themeColors.light,
              '--theme-gradient': themeColors.gradient,
              '--theme-gradient-reverse': themeColors.gradientReverse
            }}
          >
            {/* Header */}
            <div className="widget-header">
              <div className="widget-header-content">
                <div className="widget-logo-small">
                  <img src="/logo192.png" alt="LGBT Resources" />
                </div>
                <div className="widget-title">
                  <h3>lgbt near me</h3>
                  <p>the national lgbtq+ community database</p>
                  <p className="widget-title-powered-by">
                    powered by the{' '}
                    <a 
                      href="https://www.lgbthotline.org/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      LGBT National Help Center
                    </a>
                  </p>
                  <p className="widget-title-hotline">
                    lgbt national hotline: 888-843-4564
                  </p>
                </div>
              </div>
              {!hideButton && (
                <button 
                  className="widget-close-btn"
                  onClick={handleClose}
                  aria-label="Close widget"
                >
                  ×
                </button>
              )}
            </div>

            {/* Search Form */}
            <div className={`widget-search-section ${isSearchCollapsed ? 'collapsed' : ''}`}>
              {hasSearched && resources.length > 0 && (
                <button 
                  className="widget-search-toggle"
                  onClick={() => setIsSearchCollapsed(!isSearchCollapsed)}
                  aria-label={isSearchCollapsed ? 'Expand search form' : 'Collapse search form'}
                >
                  {isSearchCollapsed ? '🔍 New Search' : '▲ Hide Search'}
                </button>
              )}
              
              <div className={`widget-search-content ${isSearchCollapsed ? 'hidden' : ''}`}>
                <div className="widget-form-group">
                  <label htmlFor="widget-zip">Enter Zip/Postal Code:</label>
                  <input
                    type="text"
                    id="widget-zip"
                    name="zip"
                    placeholder="Enter Zip/Postal Code..."
                    maxLength="7"
                    value={searchParams.zip}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    className="widget-input"
                  />
                </div>

                <div className="widget-form-row">
                  <div className="widget-form-group">
                    <label htmlFor="widget-category">Category</label>
                    <select
                      id="widget-category"
                      name="category"
                      value={searchParams.category}
                      onChange={handleInputChange}
                      className="widget-select"
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  {/* Sub-category search dropdown — currently disabled, see
                      SHOW_SUBCATEGORY_SEARCH above. Sub-categories are shown
                      on the result cards instead. */}
                  {SHOW_SUBCATEGORY_SEARCH && (() => {
                    const subs = getSubcategoriesFor(searchParams.category, youthSafe || !includeAdult);
                    if (!subs) return null;
                    return (
                      <div className="widget-form-group">
                        <label htmlFor="widget-subcategory">Subcategory</label>
                        <select
                          id="widget-subcategory"
                          name="subcategory"
                          value={searchParams.subcategory}
                          onChange={handleInputChange}
                          className="widget-select"
                        >
                          <option value={ALL_SUBCATEGORIES_LABEL}>{ALL_SUBCATEGORIES_LABEL}</option>
                          {subs.map(sub => (
                            <option key={sub} value={sub}>{sub}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })()}

                  <div className="widget-form-group">
                    <label htmlFor="widget-radius">Radius</label>
                    <select
                      id="widget-radius"
                      name="radius"
                      value={searchParams.radius}
                      onChange={handleInputChange}
                      className="widget-select"
                    >
                      <option value={1}>1 mile</option>
                      <option value={5}>5 miles</option>
                      <option value={10}>10 miles</option>
                      <option value={25}>25 miles</option>
                      <option value={50}>50 miles</option>
                      <option value={100}>100 miles</option>
                    </select>
                  </div>
                </div>


                {!youthSafe && (
                  <div className="widget-checkbox-group">
                    <label className="widget-checkbox-label">
                      <input
                        type="checkbox"
                        checked={includeAdult}
                        onChange={(e) => setIncludeAdult(e.target.checked)}
                      />
                      <span>Include 18+ venues (bars, clubs)</span>
                    </label>
                  </div>
                )}

                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className="widget-search-btn"
                >
                  {loading ? 'Searching...' : 'Search'}
                </button>

                {youthSafe && (
                  <div className="widget-youth-badge">
                    🛡️ Youth Safe Mode Active
                  </div>
                )}

                {error && (
                  <div className="widget-error">
                    {error}
                  </div>
                )}
              </div>
            </div>

            {/* Results Section with View Toggle */}
            {hasSearched && (
              <div className="widget-results-section" key={`results-${searchCount}`}>
                {/* Loading State */}
                {loading ? (
                  <div className="widget-loading-state">
                    <div className="loading-spinner"></div>
                    <p>Searching for resources...</p>
                  </div>
                ) : (
                  <>
                    {/* Results Header with View Toggle */}
                    <div className="widget-results-header">
                      <h4>
                        {resources.length} resource{resources.length !== 1 ? 's' : ''} found
                      </h4>
                      
                      {/* List/Map Toggle */}
                      {resources.length > 0 && (
                    <div className="widget-view-toggle">
                      <button
                        className={`widget-view-btn ${viewMode === 'list' ? 'active' : ''}`}
                        onClick={() => setViewMode('list')}
                        aria-label="List view"
                        title="List View"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M3 4h18v2H3V4zm0 7h18v2H3v-2zm0 7h18v2H3v-2z"/>
                        </svg>
                        <span>List</span>
                      </button>
                      <button
                        className={`widget-view-btn ${viewMode === 'map' ? 'active' : ''}`}
                        onClick={() => setViewMode('map')}
                        aria-label="Map view"
                        title="Map View"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                        </svg>
                        <span>Map</span>
                      </button>
                    </div>
                  )}
                </div>

                {resources.length > 0 ? (
                  <>
                    {/* LIST VIEW */}
                    {viewMode === 'list' && (
                      <div className="widget-results-list">
                        {resources.map((resource, index) => {
                          const resourceKey = resource.id || index;
                          return (
                            <div key={resourceKey} className="widget-resource-card">
                              <div className="resource-card-header">
                                <h5 className="resource-name">{resource.name}</h5>
                                {resource.ageRestricted && (
                                  <span className="resource-age-badge">18+</span>
                                )}
                              </div>
                              {resource.name2 && (
                                <p className="resource-name2">{resource.name2}</p>
                              )}
                              
                              {(() => {
                                const badgeLabels = buildCategoryBadgeLabels(resource);
                                if (badgeLabels.length === 0) return null;
                                return (
                                  <div className="resource-categories">
                                    {(expandedCategories[resourceKey]
                                      ? badgeLabels
                                      : badgeLabels.slice(0, 4)
                                    ).map((label, idx) => (
                                      <span key={idx} className="resource-category-badge">{label}</span>
                                    ))}
                                    {badgeLabels.length > 4 && (
                                      <button 
                                        className="resource-category-badge resource-category-more"
                                        onClick={(e) => toggleExpandedCategories(e, resourceKey)}
                                      >
                                        {expandedCategories[resourceKey] 
                                          ? 'Show less' 
                                          : `+${badgeLabels.length - 4} more`}
                                      </button>
                                    )}
                                  </div>
                                );
                              })()}
                              
                              {resource.description && (
                                <p className="resource-description">{resource.description}</p>
                              )}
                              
                              <div className="resource-details">
                                {resource.address && (
                                  <div className="resource-detail">
                                    <span className="detail-icon">📍</span>
                                    <span>{resource.address}</span>
                                  </div>
                                )}
                                
                                {resource.distance !== undefined && (
                                  <div className="resource-detail">
                                    <span className="detail-icon">📏</span>
                                    <span>{resource.distance} miles away</span>
                                  </div>
                                )}
                                
                                {resource.phone && (
                                  <div className="resource-detail">
                                    <span className="detail-icon">📞</span>
                                    <a href={`tel:${resource.phone}`}>{resource.phone}</a>
                                  </div>
                                )}
                                
                                {resource.hotline && (
                                  <div className="resource-detail">
                                    <span className="detail-icon">☎️</span>
                                    <a href={`tel:${resource.hotline}`}>{resource.hotline} (Toll-Free)</a>
                                  </div>
                                )}
                                
                                {resource.website && (
                                  <div className="resource-detail">
                                    <span className="detail-icon">🌐</span>
                                    <a 
                                      href={resource.website.match(/^https?:\/\//i) ? resource.website : `https://${resource.website}`} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                    >
                                      Visit Website
                                    </a>
                                  </div>
                                )}
                                
                                {(resource.website2 || resource.website3 || resource.note) && (
                                  <div className="resource-more-details">
                                    <button 
                                      className="more-details-toggle"
                                      onClick={(e) => toggleExpandedDetails(e, resourceKey)}
                                    >
                                      <span className="more-details-icon">
                                        {expandedDetails[resourceKey] ? '▾' : '▸'}
                                      </span>
                                      <span>More Details</span>
                                    </button>
                                    {expandedDetails[resourceKey] && (
                                      <div className="more-details-content">
                                        {resource.website2 && (() => {
                                          const platform = getPlatformFromUrl(resource.website2);
                                          return (
                                            <div className="resource-detail">
                                              <span className="detail-icon"><PlatformIcon platform={platform} /></span>
                                              <a 
                                                href={resource.website2.match(/^https?:\/\//i) ? resource.website2 : `https://${resource.website2}`} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                              >
                                                {getPlatformLabel(platform)}
                                              </a>
                                            </div>
                                          );
                                        })()}
                                        {resource.website3 && (() => {
                                          const platform = getPlatformFromUrl(resource.website3);
                                          return (
                                            <div className="resource-detail">
                                              <span className="detail-icon"><PlatformIcon platform={platform} /></span>
                                              <a 
                                                href={resource.website3.match(/^https?:\/\//i) ? resource.website3 : `https://${resource.website3}`} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                              >
                                                {getPlatformLabel(platform)}
                                              </a>
                                            </div>
                                          );
                                        })()}
                                        {resource.note && (
                                          <div className="resource-note">
                                            <span className="note-icon">📋</span>
                                            <div className="note-text">
                                              {resource.note.split('\n').map((line, i) => (
                                                <span key={i}>{line}{i < resource.note.split('\n').length - 1 && <br />}</span>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* MAP VIEW */}
                    {viewMode === 'map' && (
                      <div className="widget-map-container">
                        {resourcesWithCoords.length > 0 ? (
                          <>
                            <MapContainer
                              center={mapCenter}
                              zoom={mapZoom}
                              className="widget-map"
                              scrollWheelZoom={true}
                              style={{ height: '400px', width: '100%', borderRadius: '12px' }}
                            >
                              <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>'
                                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                              />
                              <MapController center={mapCenter} zoom={mapZoom} />
                              
                              {/* Track map movement for "Search This Area" feature */}
                              <MapMovementTracker
                                originalCenter={originalSearchCenter}
                                originalRadius={originalSearchRadius}
                                onMapMove={handleMapMove}
                                onZoomChange={handleZoomChange}
                                hasSearched={hasSearched}
                              />
                              
                              {resourcesWithCoords.map((resource, index) => {
                                const color = getCategoryColor(resource);
                                const categoryName = resource.category || (resource.categories && resource.categories[0]) || '';
                                const icon = createColoredIcon(color, resource.ageRestricted, resource.name, categoryName);
                                
                                return (
                                  <Marker
                                    key={resource.id || index}
                                    position={[resource.coordinates.lat, resource.coordinates.lng]}
                                    icon={icon}
                                    eventHandlers={{
                                      click: () => {}
                                    }}
                                  >
                                    <Popup className="widget-map-popup">
                                      <div className="popup-content">
                                        {/* Header row: name left, 18+ right — matches the list
                                            cards; keeps the badge off the category line */}
                                        <div className="popup-header">
                                          <h4>{resource.name}</h4>
                                          {resource.ageRestricted && <span className="popup-age-badge">18+</span>}
                                        </div>
                                        {(() => {
                                          const badgeLabels = buildCategoryBadgeLabels(resource);
                                          if (badgeLabels.length === 0) return null;
                                          return (
                                            <div className="popup-category-badges">
                                              {badgeLabels.map((label, idx) => (
                                                <span key={idx} className="popup-category-badge">{label}</span>
                                              ))}
                                            </div>
                                          );
                                        })()}
                                        {resource.description && <p className="popup-description">{resource.description}</p>}
                                        {resource.address && <p className="popup-address">📍 {resource.address}</p>}
                                        {resource.distance !== undefined && <p className="popup-distance">📏 {resource.distance} miles away</p>}
                                        {resource.phone && (
                                          <p className="popup-phone">
                                            📞 <a href={`tel:${resource.phone}`}>{resource.phone}</a>
                                          </p>
                                        )}
                                        {resource.website && (
                                          <a 
                                            href={resource.website.match(/^https?:\/\//i) ? resource.website : `https://${resource.website}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="popup-website-btn"
                                          >
                                            Visit Website →
                                          </a>
                                        )}
                                      </div>
                                    </Popup>
                                  </Marker>
                                );
                              })}
                            </MapContainer>
                            
                            {/* Search This Area Button - Shows when user pans outside original search area */}
                            {showSearchHereButton && !searchingNewArea && (
                              <div className="widget-search-here-container">
                                <button 
                                  className="widget-search-here-button" 
                                  onClick={handleSearchThisArea}
                                >
                                  🔍 Search This Area
                                </button>
                                <button 
                                  className="widget-search-here-dismiss"
                                  onClick={() => setShowSearchHereButton(false)}
                                  aria-label="Dismiss"
                                >
                                  ✕
                                </button>
                              </div>
                            )}
                            
                            {/* Loading indicator when searching new area */}
                            {searchingNewArea && (
                              <div className="widget-search-here-loading">
                                Searching...
                              </div>
                            )}
                            
                            {/* Category Legend - Inside map container, bottom left corner - Alphabetical Order */}
                            <div className="widget-category-legend">
                              <div className="category-legend-title">Categories</div>
                              <div className="category-legend-items">
                                {/* Bar/Club - only shown in adult mode */}
                                {!youthSafe && (
                                  <div className="category-legend-item"><span className="cat-dot" style={{background: '#FF9500'}}></span>Bar/Club</div>
                                )}
                                <div className="category-legend-item"><span className="cat-dot" style={{background: '#FFD700'}}></span>Business</div>
                                <div className="category-legend-item"><span className="cat-dot" style={{background: '#008080'}}></span>Community</div>
                                <div className="category-legend-item"><span className="cat-dot" style={{background: '#FF3B30'}}></span>Crisis</div>
                                <div className="category-legend-item"><span className="cat-dot" style={{background: '#9400D3'}}></span>Cultural</div>
                                <div className="category-legend-item"><span className="cat-dot" style={{background: '#FA8072'}}></span>Health</div>
                                <div className="category-legend-item"><span className="cat-dot" style={{background: '#0000CD'}}></span>Legal</div>
                                <div className="category-legend-item"><span className="cat-dot" style={{background: '#663399'}}></span>Religious</div>
                                <div className="category-legend-item"><span className="cat-dot" style={{background: '#8B4513'}}></span>Senior</div>
                                <div className="category-legend-item"><span className="cat-dot" style={{background: '#00CED1'}}></span>Social</div>
                                <div className="category-legend-item"><span className="cat-dot" style={{background: '#228B22'}}></span>Sports</div>
                                <div className="category-legend-item"><span className="cat-dot" style={{background: '#1E90FF'}}></span>Student</div>
                                <div className="category-legend-item"><span className="cat-dot" style={{background: '#FF00FF'}}></span>Support</div>
                                <div className="category-legend-item"><span className="cat-dot" style={{background: '#FF1493'}}></span>Transgender</div>
                                <div className="category-legend-item"><span className="cat-dot" style={{background: '#ADFF2F'}}></span>Youth</div>
                              </div>
                              {/* 18+ indicator - only shown in adult mode */}
                              {!youthSafe && (
                                <div className="category-legend-18plus">
                                  <span className="cat-ring"></span>Adult Venue
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="widget-map-no-coords">
                            <p>📍 Map view requires location data.</p>
                            <p>Some resources don't have coordinates available.</p>
                            <button 
                              className="widget-switch-to-list"
                              onClick={() => setViewMode('list')}
                            >
                              View as List Instead
                            </button>
                          </div>
                        )}
                        
                        {/* Map Legend */}
                        {/* <div className="widget-map-legend">
                          <span className="legend-title">🗺️ Tap markers for details</span>
                        </div> */}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="widget-empty-state">
                    <div className="empty-state-icon">🔍</div>
                    <p className="empty-state-title">No resources found</p>
                    {searchParams.category !== 'All Categories' ? (
                      <>
                        <p className="empty-state-detail">
                          No "{searchParams.category}" resources in this area
                        </p>
                        <div className="empty-state-suggestions">
                          <p>Try:</p>
                          <ul>
                            <li>Selecting "All Categories"</li>
                            <li>Expanding your search radius</li>
                            <li>Trying a nearby ZIP code</li>
                          </ul>
                        </div>
                      </>
                    ) : (
                      <div className="empty-state-suggestions">
                        <p>Try:</p>
                        <ul>
                          <li>Expanding your search radius</li>
                          <li>Trying a nearby ZIP code</li>
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                  </>
                )}
              </div>
            )}

            {/* Show instruction text before any search */}
            {!hasSearched && !loading && (
              <div className="widget-results-section">
                <div className="widget-instruction-state">
                  <p>Enter your Zip/Postal Code above and choose a<br />category to find resources in your area.</p>
                </div>
              </div>
            )}

            {/* National Resources Section - Compact after search */}
            <div className={`widget-national-section ${hasSearched ? 'widget-national-compact' : ''}`}>
              {!hasSearched && (
                <p className="widget-national-description">
                  Resources that provide services for the entire United States.
                </p>
              )}
              <a 
                href="https://aaron1547.wixsite.com/test-new/copy-of-result/index.html?national=Yes"
                target="_blank"
                rel="noopener noreferrer"
                className="widget-national-btn"
              >
                {hasSearched ? 'National Resources' : 'View National Resources'}
                <span className="national-btn-arrow">→</span>
              </a>
            </div>

            {/* Footer */}
            <div className="widget-footer">
              <a 
                href="https://www.lgbtnearme.org/add-resource" 
                target="_blank" 
                rel="noopener noreferrer"
                className="widget-footer-missing"
              >
                💡 Missing a resource? Let us know →
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LGBTResourcesWidget;